import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { createClient, Session } from '@supabase/supabase-js';
import {
  getConfig, getConfigVersion, getOrders, getInventory, getVendors,
  updateOrderStatus, deleteOrder, restoreOrder, inventoryAdd,
  connectionStatus, setAuthToken, setOnAuthExpired,
} from './lib/api';
import type { Vendor, OrderCounts, PaginationInfo } from './lib/types';
import { Order, OrderStatus, InventoryData, ConfigData, ConnectionStatus } from './lib/types';
import { nextStatus } from './lib/utils';
import { DashboardTab } from './components/DashboardTab';
import { InventoryTab } from './components/InventoryTab';
import { AnalyticsTab } from './components/AnalyticsTab';
import { ReportsTab }   from './components/ReportsTab';
import { SettingsTab }  from './components/SettingsTab';
import MobileLayout from './components/mobile/MobileLayout';
import { Toast } from './components/ui';
import type { ToastMessage } from './components/ui';
import { LoadingScreen, LoginScreen, SetPasswordScreen, TrialExpiredScreen } from './components/auth';
import { AppHeader } from './components/shell/AppHeader';
import { StatCards } from './components/shell/StatCards';
import { DashboardFilterBar } from './components/shell/DashboardFilterBar';
import { DuplicateBanner } from './components/dashboard/DuplicateBanner';
import { AdminShell } from './components/admin/AdminShell';
import { csvRow, escapeCsvCell } from './components/reports/csvEscape';

// Auth tokens live in window.sessionStorage rather than localStorage so they
// are cleared when the tab/window closes, narrowing the XSS exfiltration
// window for the refresh token. Trade-off: closing the tab logs the user
// out (no "remember me" across browser restarts). See decisions.md D85 and
// SECURITY_AUDIT_2026-05-06.md C-AUTH-1.
const supabase = createClient(
  import.meta.env.VITE_SUPABASE_URL as string,
  import.meta.env.VITE_SUPABASE_ANON_KEY as string,
  {
    auth: {
      persistSession: true,
      storageKey: 'gopal-auth',
      storage: typeof window !== 'undefined' ? window.sessionStorage : undefined,
    },
  }
);

const isAdminRoute = () => typeof window !== 'undefined' && window.location.pathname.startsWith('/admin');

const PAGE_SIZE = 50;

export default function App() {
  // ── Auth & session ────────────────────────────────────────────────────────
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  // ── UI state ──────────────────────────────────────────────────────────────
  const [isDark, setIsDark] = useState(() => localStorage.getItem('gopal_dark') === '1');
  // Phones use the mobile layout; tablets (≥768px) use the desktop layout
  // with tablet-adjusted proportions from mobileStyles.ts.
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  // Whitelist against the current tab set. Protects returning users whose
  // last-stored tab no longer exists (e.g. super_admins on the deleted
  // 'admin' tab pre-M4) from landing on a blank content pane.
  const VALID_TABS = ['orders', 'inventory', 'analytics', 'reports', 'settings'];
  const [activeTab, setActiveTab] = useState(() => {
    const t = localStorage.getItem('gopal_active_tab') || '';
    return VALID_TABS.includes(t) ? t : 'orders';
  });
  const [adminMode, setAdminMode] = useState(() => isAdminRoute());
  const [addOrderTrigger, setAddOrderTrigger] = useState(0);
  const [importTrigger,   setImportTrigger]   = useState(0);
  const [showOrdersUpdated, setShowOrdersUpdated] = useState(false);

  // ── Toast ─────────────────────────────────────────────────────────────────
  const [toast, setToast] = useState<ToastMessage | null>(null);
  const toastTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const showToast = useCallback((message: string, type: 'success' | 'error' = 'error') => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToast({ message, type });
    toastTimer.current = setTimeout(() => setToast(null), 5000);
  }, []);

  // ── Connection + data ────────────────────────────────────────────────────
  const [connStatus, setConnStatus] = useState<ConnectionStatus>('connecting');
  const [configData, setConfigData] = useState<ConfigData | null>(null);
  const [allOrders,  setAllOrders]  = useState<Order[]>([]);
  const [inventoryData, setInventoryData] = useState<InventoryData | null>(null);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [counts, setCounts] = useState<OrderCounts>({ pending: 0, ordered: 0, delivered: 0, closed: 0, total: 0 });
  const [currentPage, setCurrentPage] = useState(1);

  // ── Filter state ──────────────────────────────────────────────────────────
  const [search,         setSearch]         = useState('');
  const [filterStatus,   setFilterStatus]   = useState('all');
  const [filterDept,     setFilterDept]     = useState('all');
  const [filterCat,      setFilterCat]      = useState('all');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo,   setFilterDateTo]   = useState('');

  const hasInitialLoad   = React.useRef(false);
  const ordersUpdatedRef = React.useRef(false);
  const countsRef        = React.useRef<OrderCounts>({ pending: 0, ordered: 0, delivered: 0, closed: 0, total: 0 });

  // ── Invite / password flow ───────────────────────────────────────────────
  const [needsPassword, setNeedsPassword] = useState(() => {
    const hash = window.location.hash || '';
    return hash.includes('type=invite') || hash.includes('type=recovery') || hash.includes('type=magiclink');
  });

  // ── Filtered orders (client-side — D58) ──────────────────────────────────
  const filteredOrders = useMemo(() => {
    let result = allOrders;
    if (filterStatus !== 'all') result = result.filter(o => o.status.toLowerCase() === filterStatus);
    if (filterDept   !== 'all') result = result.filter(o => o.department_id === filterDept);
    if (filterCat    !== 'all') result = result.filter(o => o.category === filterCat);
    if (filterDateFrom) result = result.filter(o => (o.order_date || '') >= filterDateFrom);
    if (filterDateTo)   result = result.filter(o => (o.order_date || '') <= filterDateTo);
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(o =>
        (o.order_number || '').toLowerCase().includes(q) ||
        (o.vendor       || '').toLowerCase().includes(q) ||
        (o.staff_name   || '').toLowerCase().includes(q) ||
        (o.notes        || '').toLowerCase().includes(q) ||
        (o.order_items || []).some(i => (i.item_name || '').toLowerCase().includes(q))
      );
    }
    return result;
  }, [allOrders, filterStatus, filterDept, filterCat, filterDateFrom, filterDateTo, search]);

  const pageOrders = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredOrders.slice(start, start + PAGE_SIZE);
  }, [filteredOrders, currentPage]);

  const clientPagination = useMemo<PaginationInfo>(() => ({
    page: currentPage, limit: PAGE_SIZE,
    total: filteredOrders.length,
    total_pages: Math.max(1, Math.ceil(filteredOrders.length / PAGE_SIZE)),
  }), [filteredOrders.length, currentPage]);

  // Reset to page 1 when filters change
  useEffect(() => { setCurrentPage(1); },
    [filterStatus, filterDept, filterCat, filterDateFrom, filterDateTo, search]);

  // ── Data loading ──────────────────────────────────────────────────────────
  const refreshOrders = useCallback(async () => {
    setShowOrdersUpdated(false);
    ordersUpdatedRef.current = false;
    const result = await getOrders({ limit: 200 });
    if (result.ok) {
      setAllOrders(result.data || []);
      if (result.counts) setCounts(result.counts);
    }
  }, []);

  const loadData = useCallback(async () => {
    const cached = localStorage.getItem('gopal_config_cache');
    if (cached) { try { setConfigData(JSON.parse(cached)); } catch {/* cache corrupt */} }
    const [cfg, ord, ven, inv] = await Promise.all([
      getConfig(),
      getOrders({ limit: 200 }),
      getVendors(),
      getInventory(),
    ]);
    if (cfg.ok && cfg.data) {
      setConfigData(cfg.data);
      localStorage.setItem('gopal_config_cache', JSON.stringify(cfg.data));
    }
    if (ord.ok) {
      setAllOrders(ord.data || []);
      if (ord.counts) setCounts(ord.counts);
    }
    if (ven.ok && ven.data) setVendors(ven.data as Vendor[]);
    if (inv.ok && inv.data) setInventoryData(inv.data);
    hasInitialLoad.current = true;
  }, []);

  const handleRefresh = useCallback(async () => {
    const cfg = await getConfig();
    if (cfg.ok && cfg.data) {
      setConfigData(cfg.data);
      localStorage.setItem('gopal_config_cache', JSON.stringify(cfg.data));
    }
  }, []);

  const handleOrderAdded      = useCallback(() => { refreshOrders(); }, [refreshOrders]);
  const handleInventoryChanged = useCallback(() => {
    getInventory().then(r => { if (r.ok && r.data) setInventoryData(r.data); });
  }, []);

  const clearSessionData = useCallback(() => {
    setAllOrders([]); setInventoryData(null); setConfigData(null); setVendors([]);
    setCounts({ pending: 0, ordered: 0, delivered: 0, closed: 0, total: 0 });
    setShowOrdersUpdated(false);
    localStorage.removeItem('gopal_config_cache');
    localStorage.removeItem('gopal_dark');
    localStorage.removeItem('gopal_active_tab');
    setAuthToken(null);
  }, []);

  const handleSignOut = useCallback(async () => {
    clearSessionData();
    await supabase.auth.signOut();
  }, [clearSessionData]);

  // ── Effects ───────────────────────────────────────────────────────────────
  useEffect(() => {
    setOnAuthExpired(() => { clearSessionData(); supabase.auth.signOut(); showToast('Session expired — please sign in again'); });
  }, [clearSessionData, showToast]);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setAuthToken(data.session?.access_token ?? null);
      setSession(data.session);
      setLoading(false);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, s) => {
      setAuthToken(s?.access_token ?? null);
      setSession(s);
      if (event === 'SIGNED_OUT') { hasInitialLoad.current = false; clearSessionData(); }
      if (event === 'PASSWORD_RECOVERY' || window.location.hash.includes('type=invite')) {
        setNeedsPassword(true);
      }
    });
    return () => subscription.unsubscribe();
  }, [clearSessionData]);

  useEffect(() => {
    document.documentElement.setAttribute('data-dark', isDark ? '1' : '0');
    document.body.setAttribute('data-dark', isDark ? '1' : '0');
    localStorage.setItem('gopal_dark', isDark ? '1' : '0');
  }, [isDark]);

  useEffect(() => connectionStatus.subscribe(setConnStatus), []);
  useEffect(() => { localStorage.setItem('gopal_active_tab', activeTab); }, [activeTab]);

  // Config polling — picks up trigger-incremented config_version
  useEffect(() => {
    if (!configData?.config_version) return;
    const interval = setInterval(async () => {
      const res = await getConfigVersion();
      if (res.ok && res.data && res.data.config_version > (configData.config_version ?? 0)) {
        const cfg = await getConfig();
        if (cfg.ok && cfg.data) setConfigData(cfg.data);
      }
    }, 30000);
    return () => clearInterval(interval);
  }, [configData?.config_version]);

  // Orders-updated poll (15s)
  useEffect(() => { countsRef.current = counts; }, [counts]);
  useEffect(() => {
    if (!session) return;
    const interval = setInterval(async () => {
      if (!hasInitialLoad.current) return;
      if (ordersUpdatedRef.current) return;
      const result = await getOrders({ limit: 1 });
      if (result.ok && result.counts && result.counts.total !== countsRef.current.total) {
        ordersUpdatedRef.current = true;
        setShowOrdersUpdated(true);
      }
    }, 15000);
    return () => clearInterval(interval);
  }, [session]);

  useEffect(() => {
    if (session && !hasInitialLoad.current) loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  // React to /admin ↔ / navigation
  useEffect(() => {
    const onPop = () => setAdminMode(isAdminRoute());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // ── Derived ───────────────────────────────────────────────────────────────
  const locale         = configData?.config?.locale              || 'en-NZ';
  const currency       = configData?.config?.currency_symbol     || '$';
  const lowThreshold   = configData?.config?.low_stock_threshold || 5;
  const lowStockCount  = inventoryData?.inventory?.filter(i => i.available <= lowThreshold)?.length || 0;
  const categories     = configData?.categories || [];
  const activeDepts    = (configData?.departments ?? []).filter(d => d.active !== false);
  const activeStaff    = (configData?.directory   ?? []).filter(s => s.active !== false);
  const userRole       = configData?.profile?.role || '';
  const trialExpiry    = configData?.trial_ends_at ?? null;
  const isTrialExpired = configData?.subscription_status === 'trial' && trialExpiry && userRole !== 'super_admin' && new Date(trialExpiry) < new Date();

  // Admin-route access guard. Runs as an effect (not inline during render)
  // so it fires at most once per (adminMode, userRole) transition — the
  // old inline setTimeout fired on every re-render (config poll, orders
  // poll, StrictMode double-invoke) producing stacked toasts and dupe
  // history entries. Placed after userRole's declaration since it reads
  // that derived value.
  useEffect(() => {
    if (adminMode && userRole && userRole !== 'super_admin') {
      window.history.pushState({}, '', '/');
      setAdminMode(false);
      showToast('Admin access required');
    }
  }, [adminMode, userRole, showToast]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleStatusClick = async (order: Order, targetStatus?: OrderStatus) => {
    const next = targetStatus || nextStatus(order);
    const previousStatus = order.status;
    const prevCounts = { ...counts };
    setAllOrders(prev => prev.map(o => (o.id === order.id ? { ...o, status: next } : o)));
    setCounts(prev => {
      const u = { ...prev };
      const oKey = previousStatus.toLowerCase() as keyof OrderCounts;
      const nKey = next.toLowerCase()          as keyof OrderCounts;
      if (oKey in u && typeof u[oKey] === 'number') (u as any)[oKey]--;
      if (nKey in u && typeof u[nKey] === 'number') (u as any)[nKey]++;
      return u;
    });
    const result = await updateOrderStatus(order.id, next, order.updated_at);
    if (!result.ok) {
      setAllOrders(prev => prev.map(o => (o.id === order.id ? { ...o, status: previousStatus } : o)));
      setCounts(prevCounts);
      showToast(result.message || result.error || 'Failed to update status');
      return;
    }
    // Thread the new updated_at forward so subsequent status changes
    // on the same order use the current timestamp (server-returned).
    // Without this, the next status change sends a stale timestamp and
    // fails the version check (D57 optimistic concurrency).
    const newUpdatedAt = result.data?.updated_at;
    if (newUpdatedAt) {
      setAllOrders(prev => prev.map(o => (o.id === order.id ? { ...o, updated_at: newUpdatedAt } : o)));
    }
  };

  const handleDelete = async (id: string) => {
    const deletedOrder = allOrders.find(o => o.id === id);
    const prevCounts = { ...counts };
    setAllOrders(prev => prev.filter(o => o.id !== id));
    if (deletedOrder) {
      setCounts(prev => {
        const u = { ...prev };
        const sKey = deletedOrder.status.toLowerCase() as keyof OrderCounts;
        if (sKey in u && typeof u[sKey] === 'number') (u as any)[sKey]--;
        u.total--;
        return u;
      });
    }
    const result = await deleteOrder(id, deletedOrder?.updated_at);
    if (!result.ok) {
      if (deletedOrder) setAllOrders(prev => [...prev, deletedOrder]);
      setCounts(prevCounts);
      showToast(result.message || result.error || 'Failed to delete order');
      return;
    }
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToast({
      message: 'Order deleted',
      type: 'success',
      action: {
        label: 'Undo',
        onClick: async () => {
          if (toastTimer.current) clearTimeout(toastTimer.current);
          setToast(null);
          const restoreResult = await restoreOrder(id);
          if (restoreResult.ok && deletedOrder) {
            setAllOrders(prev => {
              if (prev.length === 0) return [deletedOrder];
              const deletedCreatedAt = deletedOrder.created_at || '';
              const insertIdx = prev.findIndex(o => (o.created_at || '') < deletedCreatedAt);
              if (insertIdx === -1) return [...prev, deletedOrder];
              return [...prev.slice(0, insertIdx), deletedOrder, ...prev.slice(insertIdx)];
            });
            setCounts(prevCounts);
            showToast('Order restored', 'success');
          } else {
            showToast(restoreResult.message || restoreResult.error || 'Failed to restore order — try refreshing');
          }
        },
      },
    });
    toastTimer.current = setTimeout(() => setToast(null), 8000);
  };

  const handleDownloadTemplate = () => {
    const cats = configData?.categories || [];
    const today = new Date().toISOString().split('T')[0];
    const deptName   = activeDepts[0]?.name || 'Warehouse';
    const catName    = cats[0] || 'PPE';
    const vendorName = vendors[0]?.name || 'Vendor Name';
    const staffName  = activeStaff[0]?.name || 'Staff Name';
    const headers = csvRow(['Order Date', 'Staff Name', 'Department', 'Vendor', 'Category', 'Reference Number', 'Status', 'Notes', 'Item Name', 'Quantity', 'Unit Price (ex GST)', 'Size', 'SKU']);
    const ex1 = csvRow([today, staffName, deptName, vendorName, catName, 'PO-00001', 'Pending', '', 'Safety Boots', 2, 89.95, 'Size 10', 'SB-1234']);
    const ref = [
      '', '# REFERENCE',
      escapeCsvCell(`# Valid Departments: ${activeDepts.map(d => d.name).join(', ')}`),
      escapeCsvCell(`# Valid Categories: ${cats.join(', ')}`),
      '# Valid Statuses: Pending, Ordered, Delivered, Closed',
    ];
    const csv = [headers, ex1, ...ref].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'gopal-order-template.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  const enterAdmin = () => {
    if (userRole !== 'super_admin') {
      showToast('Admin access required');
      return;
    }
    window.history.pushState({}, '', '/admin');
    setAdminMode(true);
  };
  const exitAdmin = () => {
    window.history.pushState({}, '', '/');
    setAdminMode(false);
  };

  // ── Early returns ─────────────────────────────────────────────────────────
  if (loading) return <LoadingScreen />;

  if (!session) return <LoginScreen supabase={supabase} />;

  if (session && needsPassword) return (
    <SetPasswordScreen supabase={supabase} onDone={() => setNeedsPassword(false)} />
  );

  if (session && !needsPassword && isTrialExpired) return (
    <TrialExpiredScreen onSignOut={handleSignOut} />
  );

  // ── Admin zone ────────────────────────────────────────────────────────────
  // Only render AdminShell once we've confirmed the user is super_admin.
  // While configData is loading (auth resolved but config hasn't arrived)
  // userRole === '' — we must NOT render admin chrome or fire admin fetches
  // speculatively. The redirect for non-super-admins is handled by the
  // useEffect above, not inline.
  if (adminMode) {
    if (!configData || userRole !== 'super_admin') {
      return <LoadingScreen />;
    }
    return (
      <>
        <Toast toast={toast} onDismiss={() => setToast(null)} />
        <AdminShell
          userEmail={session.user.email || ''}
          userName={configData.profile.display_name || session.user.email || ''}
          onExit={exitAdmin}
          onSignOut={handleSignOut}
          isDark={isDark}
          setIsDark={setIsDark}
          showToast={showToast}
        />
      </>
    );
  }

  // ── Mobile ────────────────────────────────────────────────────────────────
  if (isMobile) return (
    <>
      <Toast toast={toast} onDismiss={() => setToast(null)} />
      <MobileLayout
        session={session} configData={configData}
        inventoryData={inventoryData} vendors={vendors}
        isDark={isDark} setIsDark={setIsDark}
        onSignOut={handleSignOut}
        onOrderAdded={handleOrderAdded}
        onInventoryChanged={handleInventoryChanged}
        showToast={showToast}
      />
    </>
  );

  // ── Main shell ────────────────────────────────────────────────────────────
  const tabs = [
    { id: 'orders',    label: 'Dashboard' },
    { id: 'inventory', label: 'Inventory' },
    { id: 'analytics', label: 'Analytics' },
    { id: 'reports',   label: 'Reports' },
  ];

  return (
    <div className="flex flex-col" style={{ height: '100vh', overflow: 'hidden' }}>
      <Toast toast={toast} onDismiss={() => setToast(null)} />

      <AppHeader
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={id => {
          setActiveTab(id);
          if (id === 'inventory') getInventory().then(r => { if (r.ok && r.data) setInventoryData(r.data); });
        }}
        userEmail={session.user.email || ''}
        configData={configData}
        connStatus={connStatus}
        onReconnect={loadData}
        isDark={isDark}
        setIsDark={setIsDark}
        onSignOut={handleSignOut}
        onOpenSettings={() => setActiveTab('settings')}
        onOpenAdmin={userRole === 'super_admin' ? enterAdmin : undefined}
      />

      <div className="right">
        {activeTab === 'orders' && (
          <div style={{ padding: '16px 56px 0', flexShrink: 0 }}>
            <DuplicateBanner visible={showOrdersUpdated} onRefresh={refreshOrders} />
            <StatCards counts={counts} filterStatus={filterStatus} setFilterStatus={setFilterStatus} />
            <DashboardFilterBar
              search={search} setSearch={setSearch}
              filterStatus={filterStatus} setFilterStatus={setFilterStatus}
              filterDept={filterDept}     setFilterDept={setFilterDept}
              filterCat={filterCat}       setFilterCat={setFilterCat}
              filterDateFrom={filterDateFrom} setFilterDateFrom={setFilterDateFrom}
              filterDateTo={filterDateTo}     setFilterDateTo={setFilterDateTo}
              departments={activeDepts}
              categories={categories}
              onDownloadTemplate={handleDownloadTemplate}
              onImport={() => setImportTrigger(v => v + 1)}
              onAddOrder={() => setAddOrderTrigger(v => v + 1)}
            />
          </div>
        )}

        <div className="tc">
          {activeTab === 'orders' && (
            <DashboardTab
              filteredOrders={pageOrders}
              locale={locale} currency={currency}
              onStatusChange={handleStatusClick}
              onDelete={handleDelete}
              departments={activeDepts}
              staffDirectory={activeStaff}
              categories={categories}
              vendors={vendors}
              onOrderAdded={handleOrderAdded}
              showToast={showToast}
              addOrderTrigger={addOrderTrigger}
              importTrigger={importTrigger}
              onInventoryAdd={inventoryAdd}
              onInventoryChanged={handleInventoryChanged}
              serverPagination={clientPagination}
              onPageChange={setCurrentPage}
              inventory={inventoryData?.inventory ?? []}
            />
          )}
          {activeTab === 'inventory' && (
            <InventoryTab
              inventoryData={inventoryData}
              lowStockCount={lowStockCount}
              lowThreshold={lowThreshold}
              locale={locale}
              departments={activeDepts}
              staffDirectory={activeStaff}
              onInventoryChanged={handleInventoryChanged}
              showToast={showToast}
            />
          )}
          {activeTab === 'analytics' && (
            <AnalyticsTab
              configData={configData}
              filterCat={filterCat} setFilterCat={setFilterCat}
              filterDateFrom={filterDateFrom} setFilterDateFrom={setFilterDateFrom}
              filterDateTo={filterDateTo}     setFilterDateTo={setFilterDateTo}
              locale={locale} currency={currency}
            />
          )}
          {activeTab === 'reports' && (
            <ReportsTab
              configData={configData}
              orders={allOrders}
              locale={locale}
              currency={currency}
            />
          )}
          {activeTab === 'settings' && (
            <SettingsTab configData={configData} locale={locale} onRefresh={handleRefresh} />
          )}
        </div>
      </div>
    </div>
  );
}
